/**
 * Daily Intro Experience — the once-a-day, first-login "moment".
 *
 * macOS-setup-inspired glassmorphism, boxless: content floats directly on a
 * vibrant aurora, with the glass treatment carried by the type itself
 * (frosted gradient-clipped lettering) and by custom glass mood orbs.
 * Between steps a three-band wave curtain sweeps the screen — static curved
 * SVG bands moved by GPU transforms only, so the roll stays at full frame
 * rate. Greeting → mood → moodlets → a "your day" brief, then a focus-pull
 * dissolve into the live app.
 *
 * All motion runs through anime.js; prefers-reduced-motion gets plain fades.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import {
  LockOutlined as LockIcon,
  AutoAwesome as SparkIcon,
  Refresh as RefreshIcon,
  EventOutlined as EventIcon,
  TaskAltOutlined as TaskIcon,
} from '@mui/icons-material';
import { animate, stagger } from 'animejs';
import apiClient from '../../services/api/config';
import * as wellnessAPI from '../../services/api/wellnessAPI';
import { buildMyDay, MyDayItem } from '../dashboard/myDay.logic';
import { GlassSpinner } from '../common/GlassLoader';
import { getMotionTier, softBlob } from '../common/motionTier';
import GlassMoodFace from './GlassMoodFace';
import { buildIntroCopy } from './introCopy';
import { paletteForInterests, readPalette, savePalette } from '../onboarding/journey/paletteStore';
import {
  IntroPhase,
  INTRO_SOURCE,
  MOOD_OPTIONS,
  STATE_OPTIONS,
  buildAnswers,
  fallbackTechniques,
  buildTrendDigest,
  localDateISO,
  Technique,
  TrendDigest,
} from './dailyIntro.logic';

const SERIF = "'Iowan Old Style','Palatino Linotype',Palatino,Georgia,serif";
const BLUE_GRADIENT = 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)';
const BRAND_BLUE = '#3B82F6';

const PHASE_PROGRESS: Record<IntroPhase, number> = {
  boot: 6, greet: 16, mood: 42, states: 66, reveal: 90, exit: 100,
};

// The glass is in the letters, not a box: smoked-glass gradient clipped to
// the glyphs, a hairline of light underneath, soft depth behind. Built from
// the register-aware --ink token so it reads in light AND dark themes.
const GLASS_TEXT = {
  background: 'linear-gradient(150deg, color-mix(in srgb, var(--ink) 92%, transparent) 8%, color-mix(in srgb, var(--ink) 46%, transparent) 48%, color-mix(in srgb, var(--ink) 80%, transparent) 82%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  color: 'transparent',
  // One hairline of light + one modest shadow. Every word is its own layer
  // while it animates, so the shadow radius is kept small on purpose.
  filter: 'drop-shadow(0 1px 0 rgba(255,255,255,0.55)) drop-shadow(0 6px 14px rgba(15,23,42,0.18))',
} as const;

// Aurora blobs: pre-soft radial gradients, NOT filter: blur(). A blurred
// 60vmax layer has to be re-filtered every frame it moves; a gradient is
// rasterised once and then only translated/scaled by the compositor.
const DEFAULT_AURORA = [
  { color: '#60A5FA', size: '66vmax', pos: { top: '-22%', left: '-18%' }, opacity: 0.6 },
  { color: '#A78BFA', size: '58vmax', pos: { bottom: '-28%', right: '-16%' }, opacity: 0.55 },
  { color: '#22D3EE', size: '48vmax', pos: { top: '22%', right: '8%' }, opacity: 0.42 },
  { color: '#F472B6', size: '46vmax', pos: { bottom: '0%', left: '2%' }, opacity: 0.36 },
  { color: '#FBBF24', size: '40vmax', pos: { top: '0%', left: '36%' }, opacity: 0.26 },
] as const;

// Wave curtain band: a static path with rolling left + right edges, wider
// than the screen, swept across with translateX only (compositor-friendly —
// no per-frame path recomputation, so the wave stays at full frame rate).
const WAVE_BAND_PATH =
  'M 168 0 C 96 170, 232 330, 148 500 C 76 660, 216 830, 132 1000 ' +
  'L 1352 1000 C 1288 828, 1420 656, 1340 500 C 1272 348, 1404 172, 1330 0 Z';

const WAVE_DURATION = 1550; // "slightly slower" than the first cut
const WAVE_STAGGER = 110;

interface RevealData {
  digest: TrendDigest;
  techniques: Technique[];
  pointsAwarded: number | null;
  saved: boolean;
  dayItems: MyDayItem[];
}

export interface DailyIntroExperienceProps {
  firstName: string;
  /** Used to look up the palette this person chose at their first run. */
  userId?: number | string;
  streak: number;
  /** Mount the app behind the overlay just before the dissolve. */
  onMountApp: () => void;
  /** Overlay finished dissolving — safe to unmount. */
  onFinished: () => void;
  /** Persist the day's outcome ('done' | 'skipped'). */
  onComplete: (mark: 'done' | 'skipped') => void;
}

export default function DailyIntroExperience({
  firstName,
  userId,
  streak,
  onMountApp,
  onFinished,
  onComplete,
}: DailyIntroExperienceProps) {
  const [phase, setPhase] = useState<IntroPhase>('greet');
  const [mood, setMood] = useState<string>('');
  const [states, setStates] = useState<string[]>([]);
  const [reveal, setReveal] = useState<RevealData | null>(null);
  const [techniqueIndex, setTechniqueIndex] = useState(0);

  const overlayRef = useRef<HTMLDivElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef<HTMLDivElement>(null);
  const trendBarsRef = useRef<HTMLDivElement>(null);
  const waveWrapRef = useRef<HTMLDivElement>(null);
  const burstRef = useRef<HTMLDivElement>(null);
  const washRef = useRef<HTMLDivElement>(null);
  const exitingRef = useRef(false);
  const transitioningRef = useRef(false);

  // The room somebody built at their first run, if they built one. Opening
  // the app each morning to the palette you chose yourself is the difference
  // between a theme and a decoration — so the check-in wears it, keeping the
  // default sky for anyone who has not answered the journey.
  const [palette, setPalette] = useState<string[] | null>(() => readPalette(userId));

  // localStorage is the fast path, but it is empty on a new browser. The
  // interests behind the palette are on the server, so rebuild from those and
  // write the local copy back for next time.
  useEffect(() => {
    if (palette?.length || userId === undefined) return;
    let cancelled = false;
    apiClient.get('/microlearning/learning-profile')
      .then((r) => {
        const interests = r.data?.data?.interests;
        if (cancelled || !Array.isArray(interests) || !interests.length) return;
        const rebuilt = paletteForInterests(interests);
        savePalette(userId, rebuilt);
        setPalette(rebuilt);
      })
      .catch(() => { /* the default sky is a fine answer */ });
    return () => { cancelled = true; };
  }, [userId, palette]);

  const auroraBlobs = useMemo(() => {
    if (!palette?.length) return DEFAULT_AURORA;
    return DEFAULT_AURORA.map((b, i) => ({ ...b, color: palette[i % palette.length] }));
  }, [palette]);

  // 'full' = the few small filter tweens allowed; 'lite' = transform/opacity
  // only; 'still' = reduced motion. Nothing here may drop a frame.
  const tier = useMemo(() => getMotionTier(), []);
  const reducedMotion = tier === 'still';

  // Every line rolled once per mount so the moment reads differently each day.
  const copy = useMemo(() => buildIntroCopy(new Date().getHours()), []);
  const dateLine = useMemo(
    () => new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase(),
    []
  );
  const moodMeta = MOOD_OPTIONS.find((m) => m.value === mood);
  const greetWords = useMemo(
    () => `${copy.salutation}, ${firstName || 'there'}.`.split(' '),
    [copy.salutation, firstName]
  );

  // ── Lock the page behind the moment ─────────────────────────────────────
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ── Aurora: soft light blobs drifting behind the type (transform only) ──
  useEffect(() => {
    if (reducedMotion || !bgRef.current) return;
    const blobs = bgRef.current.querySelectorAll('[data-di-blob]');
    const anims = Array.from(blobs).map((b, i) =>
      animate(b, {
        translateX: [0, i % 2 === 0 ? 90 : -110, 0],
        translateY: [0, i % 2 === 0 ? -70 : 90, 0],
        scale: [1, 1.18, 1],
        duration: 16000 + i * 3500,
        ease: 'inOutSine',
        loop: true,
      })
    );
    return () => anims.forEach((a) => a.cancel());
  }, [reducedMotion]);

  // ── Mood wash fades into the aurora once a mood is picked (opacity only) ─
  useEffect(() => {
    if (!mood || !washRef.current) return;
    const a = animate(washRef.current, {
      opacity: [0, 0.32],
      duration: reducedMotion ? 0 : 1200,
      ease: 'outQuad',
    });
    return () => { a.cancel(); };
  }, [mood, reducedMotion]);

  // ── Progress hairline tracks the phase (scaleX, never width) ────────────
  useEffect(() => {
    if (!progressRef.current) return;
    animate(progressRef.current, {
      scaleX: PHASE_PROGRESS[phase] / 100,
      duration: reducedMotion ? 0 : 700,
      ease: 'outQuint',
    });
  }, [phase, reducedMotion]);

  // ── Scene breath on first mount ─────────────────────────────────────────
  useEffect(() => {
    if (!panelRef.current) return;
    animate(panelRef.current, {
      opacity: [0, 1],
      ...(reducedMotion ? {} : { scale: [0.96, 1] }),
      duration: reducedMotion ? 240 : 1100,
      ease: 'outExpo',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Greeting: words rise out of blur, macOS-hello style ─────────────────
  useEffect(() => {
    if (phase !== 'greet' || !stepRef.current) return;
    const words = stepRef.current.querySelectorAll('[data-di-word]');
    const rest = stepRef.current.querySelectorAll('[data-di-after]');
    if (reducedMotion) {
      animate([...Array.from(words), ...Array.from(rest)], { opacity: [0, 1], duration: 240 });
      return;
    }
    // The blur-rise is the one filter tween we keep: a handful of word-sized
    // layers. Only on 'full' tier; 'lite' devices get the same rise, sharp.
    const a1 = animate(words, {
      opacity: [0, 1],
      translateY: [30, 0],
      scale: [1.08, 1],
      ...(tier === 'full' ? { filter: ['blur(16px)', 'blur(0px)'] } : {}),
      duration: 1150,
      delay: stagger(150, { start: 350 }),
      ease: 'outExpo',
    });
    const a2 = animate(rest, {
      opacity: [0, 1],
      translateY: [16, 0],
      duration: 800,
      delay: stagger(180, { start: 350 + words.length * 150 + 500 }),
      ease: 'outExpo',
    });
    return () => { a1.cancel(); a2.cancel(); };
  }, [phase, reducedMotion, tier]);

  // ── Step entrance for mood / states / reveal content (transform+opacity) ─
  useEffect(() => {
    if (phase === 'greet' || phase === 'exit' || !stepRef.current) return;
    const targets = stepRef.current.querySelectorAll('[data-di-item]');
    if (!targets.length) return;
    const anim = animate(targets, {
      opacity: [0, 1],
      translateY: [18, 0],
      scale: [1.02, 1],
      duration: reducedMotion ? 200 : 750,
      delay: reducedMotion ? 0 : stagger(55, { start: 60 }),
      ease: 'outExpo',
    });
    return () => { anim.cancel(); };
  }, [phase, reveal, reducedMotion]);

  // ── Trend bars grow when the reveal data lands ───────────────────────────
  useEffect(() => {
    if (phase !== 'reveal' || !reveal || !trendBarsRef.current) return;
    const bars = trendBarsRef.current.querySelectorAll('[data-di-bar]');
    if (!bars.length) return;
    const anim = animate(bars, {
      scaleY: [0, 1],
      duration: reducedMotion ? 0 : 900,
      delay: reducedMotion ? 0 : stagger(70, { start: 300 }),
      ease: 'outBack',
    });
    return () => { anim.cancel(); };
  }, [phase, reveal, reducedMotion]);

  // ── The wave curtain: three curved bands sweep across on transforms only.
  //    `onCover` fires while the screen is fully blanketed; `onDone` when
  //    the last band has left. Shared by step changes and the grand exit. ──
  const runWave = useCallback((onCover: () => void, onDone: () => void, duration = WAVE_DURATION) => {
    const wrap = waveWrapRef.current;
    if (!wrap) { onCover(); onDone(); return; }
    const bands = wrap.querySelectorAll('[data-di-band]');
    wrap.style.display = 'block';
    let covered = false;
    animate(bands, {
      translateX: ['-112%', '178%'],
      duration,
      delay: stagger(WAVE_STAGGER),
      ease: 'inOutQuint',
      onUpdate: (self) => {
        // Just past the temporal midpoint of the middle band the leading
        // band already blankets the screen.
        if (!covered && self.currentTime >= WAVE_STAGGER + duration * 0.5) {
          covered = true;
          onCover();
        }
      },
      onComplete: () => {
        if (!covered) { covered = true; onCover(); }
        wrap.style.display = 'none';
        onDone();
      },
    });
  }, []);

  const transitionTo = useCallback((next: IntroPhase, after?: () => void) => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    const swap = () => {
      setPhase(next);
      after?.();
    };
    if (reducedMotion) {
      swap();
      transitioningRef.current = false;
      return;
    }
    runWave(swap, () => { transitioningRef.current = false; });
    if (panelRef.current) {
      animate(panelRef.current, {
        scale: [1, 0.985, 1],
        duration: WAVE_DURATION + 2 * WAVE_STAGGER,
        ease: 'inOutQuad',
      });
    }
  }, [reducedMotion, runWave]);

  // Reset the step container's styles whenever a new phase renders.
  useEffect(() => {
    const el = stepRef.current;
    if (!el) return;
    el.style.opacity = '1';
    el.style.transform = 'none';
    el.style.filter = 'none';
  }, [phase]);

  // ── Enter advances the greeting ─────────────────────────────────────────
  const advanceFromGreet = useCallback(() => {
    if (phase !== 'greet') return;
    transitionTo('mood');
  }, [phase, transitionTo]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && phase === 'greet') advanceFromGreet();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, advanceFromGreet]);

  // ── Mood selection ───────────────────────────────────────────────────────
  const selectMood = (value: string) => {
    if (mood || transitioningRef.current) return;
    setMood(value);
    const root = stepRef.current;
    if (root && !reducedMotion) {
      const chosen = root.querySelector(`[data-di-mood="${value}"]`);
      const others = root.querySelectorAll(`[data-di-mood]:not([data-di-mood="${value}"])`);
      if (chosen) animate(chosen, { scale: [1, 1.22, 1.1], duration: 480, ease: 'outBack' });
      if (others.length) animate(others, { opacity: 0.15, scale: 0.9, duration: 400, ease: 'outQuad' });
    }
    window.setTimeout(() => transitionTo('states'), reducedMotion ? 120 : 560);
  };

  // ── Submit + gather the day brief ────────────────────────────────────────
  const beginReveal = useCallback(() => {
    transitionTo('reveal', async () => {
      const moodLabel = MOOD_OPTIONS.find((m) => m.value === mood)?.label || mood;
      const [submitRes, summaryRes, aiRes, eventsRes, tasksRes] = await Promise.allSettled([
        wellnessAPI.submitCheckIn({
          mood,
          answers: buildAnswers(mood, moodLabel, states),
          tags: states,
          source: INTRO_SOURCE,
        }),
        wellnessAPI.getUserSummary('7d'),
        apiClient.post('/ai/wellness-techniques', { mood, states }, { timeout: 6500 }),
        apiClient.get('/calendar/events'),
        apiClient.get('/tasks/my-tasks'),
      ]);

      const saved = submitRes.status === 'fulfilled';
      const pointsAwarded = saved ? Number((submitRes.value as any)?.pointsAwarded) || null : null;
      const series = summaryRes.status === 'fulfilled' ? summaryRes.value?.series || [] : [];
      let techniques: Technique[] = [];
      if (aiRes.status === 'fulfilled') {
        const list = (aiRes.value as any)?.data?.data;
        if (Array.isArray(list) && list.length > 0 && list[0]?.title) techniques = list;
      }
      if (!techniques.length) techniques = fallbackTechniques(mood);

      const events = eventsRes.status === 'fulfilled' && Array.isArray((eventsRes.value as any)?.data?.data)
        ? (eventsRes.value as any).data.data : [];
      const tasks = tasksRes.status === 'fulfilled' && Array.isArray((tasksRes.value as any)?.data?.data)
        ? (tasksRes.value as any).data.data : [];

      setTechniqueIndex(0);
      setReveal({
        digest: buildTrendDigest(series, localDateISO()),
        techniques,
        pointsAwarded,
        saved,
        dayItems: buildMyDay(events, tasks, new Date(), 5),
      });
      // Even a failed save marks the day handled locally: never trap the user.
      onComplete('done');
    });
  }, [mood, states, onComplete, transitionTo]);

  // ── Exit: the grand finale. Content lifts away, the full wave curtain
  //    sweeps the screen one last time, the app mounts under cover, and as
  //    the bands leave the dashboard shows through a light burst and a thin
  //    veil that dissolves. The veil is a flat translucent colour faded by
  //    opacity — NOT a backdrop-filter rack, which re-blurs the whole
  //    viewport every frame and is the single easiest way to drop frames. ─
  const runExit = useCallback(() => {
    if (exitingRef.current) return;
    exitingRef.current = true;
    setPhase('exit');
    const overlay = overlayRef.current;
    const bg = bgRef.current;
    const panel = panelRef.current;
    if (reducedMotion || !overlay) {
      onMountApp();
      if (overlay) {
        animate(overlay, { opacity: 0, duration: 320, ease: 'outQuad', onComplete: () => onFinished() });
      } else {
        onFinished();
      }
      return;
    }
    if (panel) {
      animate(panel, {
        opacity: [1, 0],
        scale: [1, 1.06],
        duration: 480,
        ease: 'inQuad',
      });
    }
    runWave(
      () => {
        // Under full cover: swap the world. Aurora out, app in behind a veil.
        onMountApp();
        if (bg) bg.style.display = 'none';
        if (panel) panel.style.display = 'none';
        overlay.style.background = 'color-mix(in srgb, var(--surface) 74%, transparent)';
      },
      () => {
        // Bands are gone — burst of light, veil dissolves, dashboard is live.
        if (burstRef.current) {
          animate(burstRef.current, { opacity: [0.9, 0], duration: 750, ease: 'outQuad' });
        }
        animate(overlay, {
          opacity: [1, 0],
          duration: 950,
          ease: 'inOutQuint',
          onComplete: () => onFinished(),
        });
      },
      1400
    );
  }, [onMountApp, onFinished, reducedMotion, runWave]);

  const skipToday = () => {
    onComplete('skipped');
    runExit();
  };

  const toggleState = (s: string) =>
    setStates((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  const technique = reveal?.techniques[techniqueIndex % (reveal?.techniques.length || 1)];
  const scoreColor = (score: number) => {
    const idx = Math.min(4, Math.max(0, Math.round(score) - 1));
    return MOOD_OPTIONS[idx].color;
  };

  const contentMaxWidth = phase === 'reveal' ? 780 : 680;

  return (
    <Box
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Daily check-in"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2500,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        color: 'var(--ink)',
        background: 'var(--surface-subtle)',
        px: 2,
      }}
    >
      {/* aurora ground — vibrant soft-glass wash. Every blob is a plain
          gradient layer moved by transform only; `contain: strict` keeps
          their movement from ever invalidating the content above. */}
      <Box ref={bgRef} aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden', contain: 'strict' }}>
        {auroraBlobs.map((b, i) => (
          <Box
            key={`${b.color}-${i}`}
            data-di-blob
            sx={{
              position: 'absolute', width: b.size, height: b.size, ...b.pos,
              borderRadius: '50%', opacity: b.opacity,
              background: softBlob(b.color),
              willChange: 'transform', transform: 'translateZ(0)',
            }}
          />
        ))}
        {/* mood wash joins the aurora once a mood is picked — faded in by opacity */}
        <Box
          ref={washRef}
          sx={{
            position: 'absolute', width: '60vmax', height: '60vmax', top: '-10%', left: '20%',
            borderRadius: '50%', opacity: 0, willChange: 'opacity',
            background: moodMeta ? softBlob(moodMeta.color) : 'none',
          }}
        />
        {/* quiet clearing behind the type — a glow, not a box */}
        <Box sx={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse 62% 54% at 50% 46%, color-mix(in srgb, var(--surface) 62%, transparent) 0%, transparent 74%)',
        }} />
      </Box>

      {/* progress hairline — scaled, never re-laid-out */}
      <Box aria-hidden sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'color-mix(in srgb, var(--hairline) 50%, transparent)' }}>
        <Box ref={progressRef} sx={{ width: '100%', height: '100%', background: BLUE_GRADIENT, transformOrigin: 'left center', transform: 'scaleX(0.06)', willChange: 'transform' }} />
      </Box>

      {/* floating content — no panel, the glass lives in the type */}
      <Box
        ref={panelRef}
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: contentMaxWidth,
          textAlign: 'center',
          // vh on iOS counts the area behind the browser toolbars, so the
          // bottom of the step was clipped; dvh tracks what is actually visible.
          maxHeight: '90vh',
          '@supports (height: 100dvh)': { maxHeight: '90dvh' },
          overflowY: 'auto',
          // No visible scrollbar — during blur/scale transitions content can
          // momentarily overflow and the bar would flash into view.
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          px: { xs: 1, sm: 2 },
          py: 2,
        }}
      >
        <Box ref={stepRef}>
          {/* ── GREET ── */}
          {phase === 'greet' && (
            <>
              <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.22em', color: 'var(--ink-faint)', mb: 3, opacity: 0 }} data-di-word>
                {dateLine}
              </Typography>
              <Box sx={{ mb: 2 }}>
                {greetWords.map((w, i) => (
                  <Typography
                    key={`${w}-${i}`}
                    component="span"
                    data-di-word
                    sx={{
                      ...GLASS_TEXT,
                      display: 'inline-block',
                      fontFamily: SERIF,
                      fontWeight: 600,
                      fontSize: 'clamp(38px, 6vw, 66px)',
                      letterSpacing: '-0.02em',
                      lineHeight: 1.12,
                      mr: '0.28em',
                      opacity: 0,
                      willChange: 'transform, filter, opacity',
                    }}
                  >
                    {w}
                  </Typography>
                ))}
              </Box>
              <Typography data-di-after sx={{ color: 'var(--ink-muted)', fontSize: 16, mb: 4, opacity: 0 }}>
                {copy.subline}
              </Typography>
              <Box data-di-after sx={{ opacity: 0 }}>
                <Button
                  onClick={advanceFromGreet}
                  variant="contained"
                  size="large"
                  sx={{
                    borderRadius: 999, px: 5, py: 1.4, fontWeight: 700, textTransform: 'none', fontSize: 16,
                    background: BLUE_GRADIENT, boxShadow: '0 12px 32px rgba(59,130,246,0.35)',
                  }}
                >
                  {copy.beginLabel}
                </Button>
                <Typography sx={{ mt: 2, fontSize: 12, color: 'var(--ink-faint)', '@media (hover: none)': { display: 'none' } }}>
                  press Enter ↵
                </Typography>
              </Box>
            </>
          )}

          {/* ── MOOD ── */}
          {phase === 'mood' && (
            <>
              <Typography data-di-item sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.22em', color: 'var(--ink-faint)' }}>
                {dateLine} · {copy.moodEyebrow}
              </Typography>
              <Typography
                data-di-item
                component="h2"
                sx={{ ...GLASS_TEXT, fontFamily: SERIF, fontWeight: 600, fontSize: 'clamp(28px,4.4vw,44px)', letterSpacing: '-0.015em', mt: 1.5, mb: 4.5 }}
              >
                {copy.moodPrompt}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 1.5, sm: 3 }, flexWrap: 'wrap' }}>
                {MOOD_OPTIONS.map((option) => (
                  <Box key={option.value} data-di-item sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box
                      component="button"
                      data-di-mood={option.value}
                      onClick={() => selectMood(option.value)}
                      aria-label={option.label}
                      sx={{
                        border: 'none',
                        background: 'none',
                        p: 0,
                        cursor: 'pointer',
                        lineHeight: 0,
                        borderRadius: '50%',
                        transition: 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
                        '&:hover': { transform: 'translateY(-4px) scale(1.05)' },
                        '&:hover [data-face]': {
                          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.5), 0 0 0 2px ${option.color}, 0 14px 30px ${option.color}44`,
                          background: `color-mix(in srgb, ${option.color} 12%, color-mix(in srgb, var(--surface) 46%, transparent))`,
                        },
                        '&:focus-visible': { outline: 'none' },
                        '&:focus-visible [data-face]': { boxShadow: `0 0 0 3px ${option.color}` },
                      }}
                    >
                      <GlassMoodFace mood={option.value} color={option.color} size={76} active={mood === option.value} />
                    </Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.01em', color: mood === option.value ? option.color : 'var(--ink-muted)' }}>
                      {copy.moodLabels[option.value]}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/* ── STATES ── */}
          {phase === 'states' && (
            <>
              <Typography data-di-item sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.22em', color: 'var(--ink-faint)' }}>
                {copy.statesEyebrow}
              </Typography>
              <Typography
                data-di-item
                component="h2"
                sx={{ ...GLASS_TEXT, fontFamily: SERIF, fontWeight: 600, fontSize: 'clamp(28px,4.4vw,44px)', letterSpacing: '-0.015em', mt: 1.5, mb: 1 }}
              >
                {copy.statesPrompt}
              </Typography>
              {moodMeta && (
                <Box data-di-item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 1, mb: 3, color: 'var(--ink-muted)' }}>
                  <SparkIcon sx={{ fontSize: 16, color: BRAND_BLUE, mt: '3px' }} />
                  <Typography sx={{ fontSize: 14, maxWidth: 440, textAlign: 'left' }}>{copy.responses[moodMeta.value]}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, maxWidth: 520, mx: 'auto', mb: 4 }}>
                {STATE_OPTIONS.map((s) => {
                  const selected = states.includes(s);
                  return (
                    <Chip
                      key={s}
                      data-di-item
                      label={s}
                      onClick={() => toggleState(s)}
                      clickable
                      sx={{
                        borderRadius: 999,
                        fontWeight: selected ? 700 : 500,
                        border: `1px solid ${selected ? BRAND_BLUE : 'color-mix(in srgb, var(--hairline) 70%, transparent)'}`,
                        backgroundColor: selected ? 'var(--tint-blue)' : 'color-mix(in srgb, var(--surface) 55%, transparent)',
                        color: selected ? 'var(--on-blue)' : 'var(--ink)',
                        '&:hover': { backgroundColor: selected ? 'var(--tint-blue)' : 'color-mix(in srgb, var(--surface) 60%, transparent)' },
                      }}
                    />
                  );
                })}
              </Box>
              <Button
                data-di-item
                onClick={beginReveal}
                variant="contained"
                size="large"
                sx={{
                  borderRadius: 999, px: 5, py: 1.3, fontWeight: 700, textTransform: 'none', fontSize: 15,
                  background: BLUE_GRADIENT, boxShadow: '0 12px 32px rgba(59,130,246,0.35)',
                }}
              >
                {states.length ? 'Log it' : 'Nothing in particular — log it'}
              </Button>
            </>
          )}

          {/* ── REVEAL: loading ── */}
          {phase === 'reveal' && !reveal && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
              <Box data-di-item sx={{ lineHeight: 0 }}>
                <GlassSpinner size={64} aria-label={copy.shaping} />
              </Box>
              <Typography data-di-item sx={{ color: 'var(--ink-muted)', fontSize: 15 }}>
                {copy.shaping}
              </Typography>
            </Box>
          )}

          {/* ── REVEAL: the day brief, boxless ── */}
          {phase === 'reveal' && reveal && (
            <>
              <Typography data-di-item sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.22em', color: 'var(--ink-faint)' }}>
                LOGGED{reveal.pointsAwarded ? ` · +${reveal.pointsAwarded} PTS` : ''}{streak > 0 ? ` · ${streak}-DAY STREAK` : ''}
              </Typography>
              <Typography
                data-di-item
                component="h2"
                sx={{ ...GLASS_TEXT, fontFamily: SERIF, fontWeight: 600, fontSize: 'clamp(24px,3.6vw,36px)', letterSpacing: '-0.015em', mt: 1.5, mb: 3.5 }}
              >
                {copy.revealHeading(firstName)}
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1.2fr 1fr' }, gap: { xs: 3, sm: 5 }, textAlign: 'left', mb: 3.5 }}>
                {/* today's agenda — floating list */}
                <Box data-di-item>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', color: 'var(--ink-faint)', mb: 1 }}>
                    YOUR DAY
                  </Typography>
                  {reveal.dayItems.length === 0 ? (
                    <Typography sx={{ fontSize: 13.5, color: 'var(--ink-muted)', py: 1 }}>
                      Clear runway — nothing scheduled or due today.
                    </Typography>
                  ) : (
                    reveal.dayItems.map((item, i) => (
                      <Box
                        key={item.key}
                        sx={{
                          display: 'flex', alignItems: 'baseline', gap: 1.25, py: 0.9,
                          borderTop: i === 0 ? 'none' : '1px solid color-mix(in srgb, var(--ink) 10%, transparent)',
                        }}
                      >
                        <Typography sx={{
                          fontSize: 11.5, fontWeight: 800, minWidth: 52, textAlign: 'right',
                          color: item.overdue ? 'var(--on-red)' : 'var(--ink-faint)',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {item.timeLabel}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)' }}>
                          {item.title}
                        </Typography>
                        <Box sx={{ ml: 'auto', flexShrink: 0, color: 'var(--ink-faint)', display: 'flex' }} aria-label={item.source}>
                          {item.source === 'Calendar'
                            ? <EventIcon sx={{ fontSize: 14 }} />
                            : <TaskIcon sx={{ fontSize: 14 }} />}
                        </Box>
                      </Box>
                    ))
                  )}
                </Box>

                {/* mood trend — floating */}
                <Box data-di-item>
                  <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', color: 'var(--ink-faint)', mb: 1 }}>
                    MOOD · PAST 7 DAYS
                  </Typography>
                  <Box ref={trendBarsRef} sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, height: 64, mb: 1 }}>
                    {reveal.digest.days.map((d, i) => {
                      const isToday = i === reveal.digest.days.length - 1;
                      const dayLetter = new Date(`${d.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'narrow' });
                      return (
                        <Box key={d.date} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, flex: 1 }}>
                          {d.score !== null ? (
                            <Box data-di-bar sx={{
                              width: 10, height: 8 + (d.score / 5) * 40, borderRadius: 5,
                              background: scoreColor(d.score), opacity: 0.92, transformOrigin: 'bottom',
                              boxShadow: `0 4px 12px ${scoreColor(d.score)}55`,
                            }} />
                          ) : (
                            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--ink-faint)', opacity: 0.5 }} />
                          )}
                          <Typography sx={{ fontSize: 9.5, fontWeight: isToday ? 800 : 500, color: isToday ? 'var(--ink)' : 'var(--ink-faint)' }}>
                            {dayLetter}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                  <Typography sx={{ fontSize: 12.5, color: 'var(--ink-muted)', lineHeight: 1.5 }}>
                    {reveal.digest.text}
                  </Typography>
                </Box>
              </Box>

              {/* one recommendation — floating */}
              {technique && (
                <Box data-di-item sx={{ maxWidth: 480, mx: 'auto', mb: 3.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', color: 'var(--ink-faint)' }}>
                      ONE THING FOR TODAY
                    </Typography>
                    {reveal.techniques.length > 1 && (
                      <Box
                        component="button"
                        onClick={() => setTechniqueIndex((i) => i + 1)}
                        aria-label="Show another suggestion"
                        sx={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--ink-faint)', display: 'flex', p: 0.25, '&:hover': { color: 'var(--ink)' } }}
                      >
                        <RefreshIcon sx={{ fontSize: 15 }} />
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ ...GLASS_TEXT, fontFamily: SERIF, fontWeight: 600, fontSize: 19, mb: 0.5 }}>
                    {technique.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: 'var(--ink-muted)', lineHeight: 1.6 }}>
                    {technique.action}
                  </Typography>
                </Box>
              )}

              {!reveal.saved && (
                <Typography data-di-item sx={{ fontSize: 12, color: 'var(--on-amber)', mb: 2 }}>
                  Couldn&apos;t save your check-in just now — we&apos;ll ask again tomorrow.
                </Typography>
              )}

              <Button
                data-di-item
                onClick={runExit}
                variant="contained"
                size="large"
                sx={{
                  borderRadius: 999, px: 5, py: 1.4, fontWeight: 700, textTransform: 'none', fontSize: 16,
                  background: BLUE_GRADIENT, boxShadow: '0 12px 32px rgba(59,130,246,0.35)',
                }}
              >
                Start my day
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* wave curtain for between-step transitions — static curved bands,
          swept by transform only for a full-frame-rate roll */}
      <Box
        ref={waveWrapRef}
        aria-hidden
        sx={{ position: 'absolute', inset: 0, zIndex: 10, pointerEvents: 'none', display: 'none', overflow: 'hidden' }}
      >
        {/* paint order: leader (frost) below, trailing deep layer on top */}
        {[
          { id: 'frost', fill: 'rgba(255,255,255,0.6)' },
          { id: 'vivid', fill: 'url(#diWaveVivid)' },
          { id: 'deep', fill: 'url(#diWaveDeep)' },
        ].map((band) => (
          <Box
            key={band.id}
            component="svg"
            data-di-band
            viewBox="0 0 1500 1000"
            preserveAspectRatio="none"
            sx={{
              position: 'absolute', top: 0, left: 0, height: '100%', width: '150%',
              transform: 'translateX(-112%)', willChange: 'transform',
            }}
          >
            {band.id === 'vivid' && (
              <defs>
                <linearGradient id="diWaveVivid" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#60A5FA" />
                  <stop offset="45%" stopColor="#8B5CF6" />
                  <stop offset="100%" stopColor="#EC4899" />
                </linearGradient>
              </defs>
            )}
            {band.id === 'deep' && (
              <defs>
                <linearGradient id="diWaveDeep" x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#22D3EE" />
                  <stop offset="100%" stopColor="#3B82F6" />
                </linearGradient>
              </defs>
            )}
            <path d={WAVE_BAND_PATH} fill={band.fill} />
          </Box>
        ))}
      </Box>

      {/* light burst for the exit reveal */}
      <Box
        ref={burstRef}
        aria-hidden
        sx={{
          position: 'absolute', inset: 0, zIndex: 11, pointerEvents: 'none', opacity: 0,
          background: 'radial-gradient(ellipse 70% 60% at 50% 45%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.4) 45%, transparent 75%)',
        }}
      />

      {/* footer: privacy + skip */}
      {(phase === 'mood' || phase === 'states') && (
        <Box sx={{ position: 'absolute', bottom: 20, left: 0, right: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', color: 'var(--ink-faint)' }}>
            <LockIcon sx={{ fontSize: 13 }} />
            <Typography sx={{ fontSize: 12 }}>Yours alone. Managers see team weather, never this.</Typography>
          </Box>
          <Button
            onClick={skipToday}
            variant="text"
            sx={{
              minHeight: 44, px: 2, borderRadius: 999, textTransform: 'none', fontSize: 13, fontWeight: 500,
              color: 'var(--ink-faint)',
              '&:hover': { color: 'var(--ink-muted)', background: 'none', textDecoration: 'underline' },
            }}
          >
            Skip for today
          </Button>
        </Box>
      )}
    </Box>
  );
}
